import { useLazyQuery } from "@apollo/client";
import { EMPLOYEE_TASKS_LIST, EMPLOYEES_BY_AGENCY_ID } from "../../api/query";
import { useEffect, useMemo, useState } from "react";
import Loader from "../../components/common/Loader";
import DynamicTable from "../../components/common/DynamicTable";
import { differenceInMinutes, endOfMonth, format, isBefore, parse, parseISO, startOfMonth } from "date-fns";
import { useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import { useAuth } from "../../context/AuthContext";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import DateSelector from "../../components/common/DateSelector";
import { showError } from "../../components/common/ToastNotification";

const employeeTaskSchema = yup.object().shape({
    assignedMemberID: yup.string().required("Employee is required"),
    assignedMember: yup.string().required("Employee is required"),
    start_date: yup.date().required("This field is required"),
    end_date: yup.date().required("This field is required")
});

const EmployeeTaskList = () => {
    const navigate = useNavigate();
    const { userInfo,isAdmin } = useAuth()

    const employeeID = userInfo?.employee_id;
    const organizationID = userInfo?.agencies?.id || '';

    const filterStartDate = localStorage.getItem("employeeTaskFilterStartDate");
    const filterEndDate = localStorage.getItem("employeeTaskFilterEndDate");
    const selectedEmployeeID = localStorage.getItem("employeeTaskEmployeeID");
    const selectedEmployeeName = localStorage.getItem("employeeTaskEmployeeName");

    //constants
    const [sorting, setSorting] = useState<SortingState>([]);
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [mainTable, setMainTable] = useState<any>(null);
    const [mainTableUpdate, setMainTableUpdate] = useState(false);
    const [assignedMemberOptions, setAssignedMemberOptions] = useState<{ key: string; name: string }[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState<number>(0);

    //api
    const [getEmployeeTaskList, { data: employeeTaskListData, loading: employeeTaskListDataLoading }] = useLazyQuery(EMPLOYEE_TASKS_LIST, {
        fetchPolicy: 'no-cache'
    })

    const [
        getEmployeesByAgencyID,
        { data: employeesData, loading: employeesDataLoading },
    ] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, {
        fetchPolicy: 'no-cache'
    });

    const {
        setValue,
        formState: { errors },
        watch,
        trigger,
        control
    } = useForm<any>({
        resolver: yupResolver(employeeTaskSchema),
        defaultValues: {
            assignedMemberID: selectedEmployeeID || '',
            assignedMember: selectedEmployeeName || '',
            end_date: filterEndDate || '',
            start_date: filterStartDate || ''
        }
    });

    //watch
    const watchAssignedMember = watch("assignedMember");
    const watchAssignedMemberID = watch('assignedMemberID');
    const watchStartDate = watch("start_date")
    const watchEndDate = watch("end_date")

    useEffect(() => {
        if (organizationID) {
            getEmployeesByAgencyID({
                variables: {
                    agency_id: parseInt(organizationID)
                }
            });
        }
    }, [organizationID])


    useEffect(() => {
        // const selectedMonth = parseInt(watchMonth);
        // const selectedYear = parseInt(watchYear);

        if (watchAssignedMemberID && watchStartDate && watchEndDate) {
            getEmployeeTaskList({
                variables: {
                    employee_id: parseInt(watchAssignedMemberID),
                    start_date: watchStartDate,
                    end_date: watchEndDate,
                    page: 1,
                    page_size:400
                }
            });
        }
    }, [watchStartDate, watchEndDate, watchAssignedMemberID]);

    useEffect(() => {
        if (employeesData && employeesData.employees_by_agency_id) {
            const employeeOptions = employeesData?.employees_by_agency_id?.employees?.map((emp: any) => ({
                key: emp.employee_id,
                name: `${emp.first_name} ${emp.last_name}`
            }));
            setAssignedMemberOptions(employeeOptions);

            const defaultMember = selectedEmployeeID || employeeOptions?.find((member: any) => member.key == employeeID);

            if (defaultMember) {
                // Set default assigned member
                setValue('assignedMemberID', defaultMember.key);
                setValue('assignedMember', defaultMember.name);
                // trigger('assignedMember');
            } else {
                // Clear if employeeID not found in members
                setValue('assignedMemberID', '');
                setValue('assignedMember', '');
            }
        }
    }, [employeesData])

    // Use useMemo to optimize the column generation
    const columns: any[] = [
        {
            key: "task_name",
            name: "Task Name",
            id: "task_name",
            header: "Task Name",
            accessorKey: "task_name",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        {
            key: "project_name",
            name: "Project Name",
            id: "project_name",
            header: "Project Name",
            accessorKey: "project_name",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        {
            key: "goal_name",
            name: "Goal Name",
            id: "goal_name",
            header: "Goal Name",
            accessorKey: "goal_name",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        {
            key: "date",
            name: "Date",
            id: "date",
            header: "Date",
            accessorKey: "date",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any) => {
                const startDateA = rowA?.getValue("date");
                const startDateB = rowB?.getValue("date");

                // If a start_date is missing (null or undefined), treat it as the latest date.
                // You can also treat missing dates as the earliest date by using `new Date(0)` instead of `new Date()`
                const defaultDate = new Date(0); // You can change this to `new Date()` if you want it sorted to the end.

                const dateA = startDateA ? parse(startDateA, "MM-dd-yyyy", new Date()) : defaultDate;
                const dateB = startDateB ? parse(startDateB, "MM-dd-yyyy", new Date()) : defaultDate;

                // Ensure valid date parsing, otherwise fallback to defaultDate
                if (isNaN(dateA.getTime())) {
                    dateA.setTime(defaultDate.getTime());
                }
                if (isNaN(dateB.getTime())) {
                    dateB.setTime(defaultDate.getTime());
                }

                // Compare the full dates directly (ascending or descending will be handled by react-table)
                return dateA.getTime() - dateB.getTime();
            },
        },
        {
            key: "status",
            name: "Status",
            id: "status",
            header: "Status",
            accessorKey: "status",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        {
            key: "tags",
            name: "Tag",
            id: "tags",
            header: "Tag",
            accessorKey: "tags",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        {
            key: "action",
            name: "Action",
            id: "action",
            header: "Action",
            accessorKey: "action",
            hideSearch: true,
            cell: ({ row }: any) => {
                const original = row.original;


                return (
                    <div className="flex items-center gap-[8px]  theme_btn_item">
                        <button
                            className="dash-action-link cursor-pointer"
                            onClick={() => {
                                navigate("/task_detail", {
                                    state: {
                                        taskId: row.original.taskID,
                                    }
                                })
                            }}
                        >
                            <span className="text-sm dark:text-white">View Task</span>
                        </button>
                    </div>
                );
            },
        },
    ]

    const formattedData = useMemo(() => {
        if (employeeTaskListData && employeeTaskListData?.employee_tasks_list?.status_code === 200) {
            setTotalPages(employeeTaskListData?.employee_tasks_list?.pagination?.total_pages)
            const employeeTaskList = employeeTaskListData?.employee_tasks_list?.tasks || [];
            return employeeTaskList.map((etl: any) => {
                return {
                    id: etl.id,
                    project_name: etl?.project_name,
                    goal_name: etl?.goal_name,
                    date: format(etl?.end_datetime, 'MM-dd-yyyy'),
                    task_name: etl?.title,
                    taskID: etl?.id,
                    status: etl?.current_status_name,
                    tags: etl?.task_tags?.length > 0 ? etl?.task_tags : "On Time"
                };
            });
        } else {
            return [];
        }
    }, [employeeTaskListData]);

    function paginationhandle(pageNo: number, itemsPerPage: number) {
        setCurrentPage(pageNo);
        getEmployeeTaskList({
            variables: {
                employee_id: parseInt(watchAssignedMemberID),
                start_date: watchStartDate,
                end_date: watchEndDate,
                page: pageNo,
                page_size: 400
            }
        })
    }

    return (
        <>
            <div id="cts-respon-p-dv" className="w-full flex flex-col items-start justify-start py-40 px-20  mq575:px-5 box-border gap-[20px] max-w-[100%] mq1425:max-w-full">
                <div className="min-w-full flex flex-col items-start justify-start gap-6">
                    <BreadCumb />

                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Employee-Task List</h3>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">



                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">

                            {isAdmin && (
                                <SearchableInputsDropdown
                                    label="Employee*"
                                    initialValue={watchAssignedMember}
                                    placeHolder="Select Member"
                                    options={assignedMemberOptions}
                                    onSelect={(key: number, name: string) => {
                                        setValue('assignedMemberID', key);
                                        setValue('assignedMember', name);
                                        localStorage.setItem("employeeTaskEmployeeID", key.toString());
                                        localStorage.setItem("employeeTaskEmployeeName", name);
                                    }}
                                    errorMessage={errors?.assignedMember}
                                     dontselectlastValue={true}
                                />
                            )}

                            <div className="status-filter pb-4 grid gap-[8px]">
                                <Controller
                                    name="start_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="Start Date*"
                                            selectedDate={field.value}
                                            onChange={(date: string | any) => {
                                                const endDate = watch("end_date");
                                                const result = true;
                                                if (result) {
                                                    field.onChange(date);
                                                    setValue("end_date", null);
                                                    setValue("start_date", date);
                                                    trigger("start_date");
                                                    localStorage.setItem("employeeTaskFilterStartDate", date);
                                                }
                                            }}
                                            nothideParsedValue={true}
                                            blueIcon={true}
                                            error={errors.start_date?.message}
                                            showAllDate={true}
                                        />
                                    )}
                                />
                            </div>
                            <div className="status-filter pb-4 grid gap-[8px]">
                                <Controller
                                    name="end_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DateSelector
                                            label="End Date*"
                                            selectedDate={field.value}
                                            onChange={(date: string | null | any) => {
                                                const date2 = parseISO(watch("start_date"));
                                                const date1 = parseISO(date);
                                                if (isBefore(date1, date2)) {
                                                    showError(
                                                        "Please select date greater than start date."
                                                    );
                                                }
                                                // else if (differenceInDays(date1, date2) > 30) {
                                                //   showError(
                                                //     "The selected date range cannot exceed 31 days."
                                                //   );
                                                // } 
                                                else {
                                                    field.onChange(date);
                                                    setValue("end_date", date);
                                                    trigger("end_date");
                                                    localStorage.setItem("employeeTaskFilterEndDate", date);
                                                }
                                            }}
                                            nothideParsedValue={true}
                                            blueIcon={true}
                                            dontopenDatepicker={!watch("start_date")}
                                            error={errors.end_date?.message}
                                            showAllDate={true}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {(formattedData?.length > 0) ? (
                            <div className="w-full emptasklist-tbl">
                                <DynamicTable
                                    filteredData={formattedData}
                                    selectedColumns={columns}
                                    sorting={sorting}
                                    setSorting={setSorting}
                                    selectedRows={selectedRows}
                                    setSelectedRows={setSelectedRows}
                                    setMainTable={setMainTable}
                                    setMainTableUpdate={setMainTableUpdate}
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    handlePageChange={paginationhandle}
                                    showPagination={true}
                                    paginationDataLength={totalPages > 1 || currentPage != 1}
                                    serialNumber={true}
                                />
                            </div>
                        ) : (
                            <div className="self-stretch rounded-lg  overflow-hidden rounded-lg flex flex-col items-center justify-center py-[20px] px-[20px] gap-[8px] text-center text-text-text-secondary mq450:pl-5 mq450:pr-5 mq450:box-border">
                                <div className="self-stretch bg-white rounded-lg bg-slate-100 overflow-hidden dark:bg-slate-700 flex flex-col items-center justify-center py-[110px] px-[53px] gap-[8px] text-center text-text-text-secondary mq450:pl-5 mq450:pr-5 mq450:box-border">
                                    {/* <img
                            className="w-9 h-9 relative overflow-hidden shrink-0"
                            alt=""
                            // src={alertOrAction === "action" ? firsflag1 : iioniconaalertcircleoutline}
                        /> */}
                                    <p className="relative my-0">No data available</p>
                                </div>
                            </div>
                        )}

                        {(employeeTaskListDataLoading || employeesDataLoading) && (
                            <Loader />
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default EmployeeTaskList; 